import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import { AGREEMENT_STATUS, AGREEMENT_TYPE } from "../../constants";
import MoneyInput from "../generic/MoneyInput";
import SelectUser from "../generic/SelectUser";
import EntityForm from "../entities/EntityForm";
import { formatNit } from "../entities/entityLogic";
import { ENTITY_TYPE_LABELS } from "../entities/entityLabels";
import { fetchEntityOptions } from "../../services/backend/EntityService";
import {
  AGREEMENT_ERROR_LABELS,
  AGREEMENT_REQUEST_ERROR_LABELS,
  AGREEMENT_STATUS_LABELS,
  AGREEMENT_TYPE_LABELS,
  formatMoney,
} from "./agreementLabels";
import { deriveBudget, normalizeCode, validateAgreement } from "./agreementLogic";
import { createAgreement, updateAgreement } from "../../services/backend/AgreementService";

const EMPTY = {
  code: "",
  type: AGREEMENT_TYPE.AGREEMENT,
  subject: "",
  // La entidad contratante se ESCOGE del registro; su razón social
  // (contractingEntity) la denormaliza el servidor y por eso no está aquí.
  entityNit: "",
  area: "",
  knowledgeTransfer: false,
  startDate: "",
  endDate: "",
  status: AGREEMENT_STATUS.UNDER_REVIEW,
  totalValue: "",
  committedValue: 0,
  managerId: null,
  managerName: "",
  managerEmail: "",
};

// Los campos que el servidor NO acepta en una edición: el código es la llave del
// documento y los valores originales son la foto que conservará el otro sí.
// `contractingEntity` va aquí porque el servidor la copia de la entidad: enviarla
// sólo serviría para que la rechazara.
const IMMUTABLE = ["code", "originalValue", "originalEndDate", "contractingEntity"];

// Alta y edición de un convenio. Valida con las MISMAS reglas que la Cloud
// Function (agreementLogic, espejo de _shared/agreementLogic), así que el
// servidor sólo debería rechazar carreras — y si lo hace, sus códigos de error
// se pintan con el mismo mapa de etiquetas.
export default function AgreementForm({ open, agreement, onClose, onSaved }) {
  const user = useSelector((state) => state.user.value);
  const isEdit = !!agreement;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [requestError, setRequestError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [entities, setEntities] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [entityFormOpen, setEntityFormOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(agreement ? { ...EMPTY, ...agreement, area: agreement.area || "" } : EMPTY);
    setErrors({});
    setRequestError(null);
  }, [open, agreement]);

  const loadEntities = useCallback(async () => {
    setEntitiesLoading(true);
    const { items } = await fetchEntityOptions();
    setEntities(items);
    setEntitiesLoading(false);
  }, []);

  useEffect(() => {
    if (open) loadEntities();
  }, [open, loadEntities]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    // El error del campo se limpia al escribir: mantenerlo mientras se corrige
    // es ruido.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  // Forma que SelectUser espera como `value`: un objeto de usuario, no el uid.
  const manager = useMemo(
    () =>
      form.managerEmail
        ? { userUID: form.managerId, email: form.managerEmail, name: form.managerName, lastName: "" }
        : null,
    [form.managerId, form.managerName, form.managerEmail]
  );

  // Forma que el Autocomplete espera como `value`: la opción entera, no el NIT.
  // Al editar un convenio cuya entidad está inactiva —y por tanto fuera de la
  // lista— se reconstruye una opción con la razón social ya denormalizada, para
  // que el campo no aparezca vacío como si nadie la hubiera escogido.
  const selectedEntity = useMemo(() => {
    if (!form.entityNit) return null;
    const found = entities.find((e) => e.nit === form.entityNit);
    if (found) return found;
    return {
      nit: form.entityNit,
      legalName: form.contractingEntity || form.entityNit,
      entityType: null,
    };
  }, [entities, form.entityNit, form.contractingEntity]);

  // Vista previa del presupuesto mientras se escribe, con la misma fórmula que
  // guardará el servidor.
  const budget = useMemo(
    () => deriveBudget({ totalValue: form.totalValue, committedValue: form.committedValue }),
    [form.totalValue, form.committedValue]
  );

  const handleSubmit = async () => {
    const { valid, errors: found } = validateAgreement(form);
    if (!valid) {
      setErrors(found);
      return;
    }
    setSaving(true);
    setRequestError(null);

    let result;
    if (isEdit) {
      const patch = { ...form };
      for (const key of IMMUTABLE) delete patch[key];
      result = await updateAgreement(agreement.code, patch, user);
    } else {
      result = await createAgreement({ ...form, code: normalizeCode(form.code) }, user);
    }
    setSaving(false);

    if (!result.ok) {
      setErrors(result.errors || {});
      // Un error de campo ya se ve sobre el campo; arriba sólo va lo que no
      // pertenece a ninguno (código repetido, permisos, red).
      if (!Object.keys(result.errors || {}).length) {
        setRequestError(
          AGREEMENT_REQUEST_ERROR_LABELS[result.error] ||
            "No se pudo guardar el convenio. Intenta de nuevo."
        );
      }
      return;
    }
    onSaved(result.data);
    onClose();
  };

  const fieldError = (name) => AGREEMENT_ERROR_LABELS[errors[name]] || undefined;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? `Editar convenio ${agreement.code}` : "Nuevo convenio"}</DialogTitle>
      <DialogContent dividers>
        {requestError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {requestError}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Código"
              value={form.code}
              onChange={(e) => setField("code", e.target.value)}
              // El código es el id del documento: cambiarlo sería otro convenio.
              disabled={isEdit}
              helperText={fieldError("code") || (isEdit ? "El código no se puede cambiar." : " ")}
              error={!!errors.code}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Tipo"
              value={form.type}
              onChange={(e) => setField("type", e.target.value)}
              helperText={fieldError("type") || " "}
              error={!!errors.type}
              fullWidth
            >
              {Object.values(AGREEMENT_TYPE).map((t) => (
                <MenuItem key={t} value={t}>{AGREEMENT_TYPE_LABELS[t]}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Estado"
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
              helperText={fieldError("status") || " "}
              error={!!errors.status}
              fullWidth
            >
              {Object.values(AGREEMENT_STATUS).map((s) => (
                <MenuItem key={s} value={s}>{AGREEMENT_STATUS_LABELS[s]}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Objeto"
              value={form.subject}
              onChange={(e) => setField("subject", e.target.value)}
              helperText={fieldError("subject") || " "}
              error={!!errors.subject}
              multiline
              minRows={2}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            {/* La entidad se escoge del registro, no se escribe: así el convenio
                queda atado a un NIT y su documentación, y no a un nombre suelto
                que cada quien teclea distinto. */}
            <Autocomplete
              options={entities}
              loading={entitiesLoading}
              value={selectedEntity}
              onChange={(_, value) => setField("entityNit", value?.nit || "")}
              getOptionLabel={(option) => option?.legalName || ""}
              isOptionEqualToValue={(option, value) => option.nit === value.nit}
              // Buscar por NIT tiene que funcionar aunque se escriba con puntos.
              filterOptions={(options, { inputValue }) => {
                const term = inputValue.trim().toLowerCase();
                if (!term) return options;
                const digits = term.replace(/\D/g, "");
                return options.filter(
                  (o) =>
                    o.legalName.toLowerCase().includes(term) ||
                    (digits && o.nit.includes(digits))
                );
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.nit}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{option.legalName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatNit(option.nit)}
                      {option.entityType ? ` · ${ENTITY_TYPE_LABELS[option.entityType]}` : ""}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Entidad contratante"
                  placeholder="Busca por NIT o razón social"
                  helperText={fieldError("entityNit") || " "}
                  error={!!errors.entityNit}
                  required
                />
              )}
              noOptionsText="No hay entidades registradas que coincidan."
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Área"
              value={form.area}
              onChange={(e) => setField("area", e.target.value)}
              helperText=" "
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              type="date"
              label="Fecha de inicio"
              value={form.startDate}
              onChange={(e) => setField("startDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={fieldError("startDate") || " "}
              error={!!errors.startDate}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              type="date"
              label="Fecha de terminación"
              value={form.endDate}
              onChange={(e) => setField("endDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={fieldError("endDate") || " "}
              error={!!errors.endDate}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <MoneyInput
              label="Valor del convenio"
              name="totalValue"
              value={form.totalValue}
              onChange={(e) => setField("totalValue", e.target.value)}
              helperText={fieldError("totalValue") || " "}
              error={!!errors.totalValue}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MoneyInput
              label="Valor comprometido"
              name="committedValue"
              value={form.committedValue}
              onChange={(e) => setField("committedValue", e.target.value)}
              helperText={
                fieldError("committedValue") ||
                `Saldo: ${formatMoney(budget.remainingValue)} · ${budget.committedPercentage}% comprometido`
              }
              error={!!errors.committedValue}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <SelectUser
              userType="users"
              placeholder="Gestor responsable"
              // SelectUser identifica la opción por correo (isOptionEqualToValue),
              // por eso el convenio guarda también managerEmail: sin él la
              // edición abriría el selector vacío.
              value={manager}
              // Se lo pasa a un Autocomplete de MUI, así que la firma es
              // (event, value).
              onChange={(event, value) => {
                setField("managerId", value?.userUID || value?.userId || null);
                setField(
                  "managerName",
                  value ? `${value.name || ""} ${value.lastName || ""}`.trim() : ""
                );
                setField("managerEmail", value?.email || "");
              }}
              noAdd
              noedit
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.knowledgeTransfer}
                  onChange={(e) => setField("knowledgeTransfer", e.target.checked)}
                />
              }
              label="Incluye transferencia de conocimiento"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {/* Una entidad sin registrar no puede bloquear el alta de un convenio:
            se registra aquí mismo y queda escogida al volver. */}
        <Stack direction="row" sx={{ flexGrow: 1 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setEntityFormOpen(true)}
            disabled={saving}>
            Registrar entidad
          </Button>
        </Stack>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </DialogActions>

      <EntityForm
        open={entityFormOpen}
        entity={null}
        onClose={() => setEntityFormOpen(false)}
        onSaved={async (saved) => {
          await loadEntities();
          if (saved?.nit) setField("entityNit", saved.nit);
        }}
      />
    </Dialog>
  );
}
